import React, { useState, useEffect } from "react";

const RecurringDepositCalculator = () => {
  const [monthlyDeposit, setMonthlyDeposit] = useState(5000);
  const [annualInterestRate, setAnnualInterestRate] = useState(7);
  const [tenure, setTenure] = useState(5);
  const [inflationRate, setInflationRate] = useState(6);
  const [result, setResult] = useState({
    futureValue: "0.00",
    totalInvestment: "0.00",
    estimatedReturns: "0.00",
    inflationAdjustedValue: "0.00",
  });

  // Recurring Deposit Calculation Function
  const calculateRD = () => {
    const P = parseFloat(monthlyDeposit);
    const r = parseFloat(annualInterestRate) / 100 / 4; // Quarterly compounding
    const n = parseFloat(tenure) * 12; // Total months
    const inflation = parseFloat(inflationRate) / 100;
    const years = parseFloat(tenure);

    if (isNaN(P) || isNaN(r) || isNaN(n) || P <= 0 || n <= 0) return;

    // RD Formula: M = P × n × (n + 1) / 2 × r / 12
    // Simplified calculation for quarterly compounding
    let maturityAmount = 0;
    for (let i = 1; i <= n; i++) {
      const quarters = (n - i + 1) / 3;
      maturityAmount += P * Math.pow(1 + r, quarters);
    }
    
    const totalDeposit = P * n;
    const interestEarned = maturityAmount - totalDeposit;
    
    // Calculate inflation-adjusted value
    const inflationAdjustedValue = maturityAmount / Math.pow(1 + inflation, years);

    setResult({
      futureValue: maturityAmount.toFixed(2),
      totalInvestment: totalDeposit.toFixed(2),
      estimatedReturns: interestEarned.toFixed(2),
      inflationAdjustedValue: inflationAdjustedValue.toFixed(2),
    });
  };

  // Run default calculation on first load
  useEffect(() => {
  calculateRD();
}, [monthlyDeposit, annualInterestRate, tenure, inflationRate]);


  const growthPercentage = result.totalInvestment > 0 
    ? ((Number(result.estimatedReturns) / Number(result.totalInvestment)) * 100).toFixed(1)
    : 0;

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
              <polyline points="16 7 22 7 22 13"></polyline>
            </svg>
          </div>
          <h1>Recurring Deposit Calculator</h1>
          <p>
            Calculate your RD maturity amount and interest earnings by investing a fixed amount every month
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                  <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                  <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                </svg>
                RD Details
              </h2>

              {/* Monthly Deposit */}
              <div className="input-group">
                <label>Monthly Deposit</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={monthlyDeposit}
                    onChange={(e) => setMonthlyDeposit(e.target.value)}
                    className="calc-input"
                    placeholder="5000"
                  />
                </div>
                <div className="quick-select-group">
                  {[2000, 5000, 10000, 20000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setMonthlyDeposit(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{(amount / 1000).toFixed(0)}K
                    </button>
                  ))}
                </div>
              </div>

              {/* Annual Interest Rate */}
              <div className="input-group">
                <label>Annual Interest Rate</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={annualInterestRate}
                    onChange={(e) => setAnnualInterestRate(e.target.value)}
                    className="calc-input"
                    placeholder="7"
                    step="0.1"
                  />
                  <span className="input-suffix">%</span>
                </div>
                <div className="quick-select-group">
                  {[5, 6, 7, 8].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setAnnualInterestRate(rate)}
                      className="quick-select-btn secondary"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Tenure */}
              <div className="input-group">
                <label>Tenure (years)</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={tenure}
                    onChange={(e) => setTenure(e.target.value)}
                    className="calc-input"
                    placeholder="5"
                  />
                </div>
                <div className="quick-select-group">
                  {[1, 3, 5, 10].map((years) => (
                    <button
                      key={years}
                      onClick={() => setTenure(years)}
                      className="quick-select-btn success"
                    >
                      {years}Y
                    </button>
                  ))}
                </div>
              </div>

              {/* Inflation Rate */}
              <div className="input-group">
                <label>Expected Inflation Rate</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={inflationRate}
                    onChange={(e) => setInflationRate(e.target.value)}
                    className="calc-input"
                    placeholder="6"
                    step="0.1"
                  />
                  <span className="input-suffix">%</span>
                </div>
              </div>

              <button onClick={calculateRD} className="calculate-btn">
                Calculate RD Returns
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                RD Summary
              </h2>

              <div className="result-card">
                {/* Total Deposit */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Total Deposit</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                        <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                        <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.totalInvestment).toLocaleString("en-IN")}
                  </div>
                </div>

                {/* Interest Earned */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Interest Earned</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                        <polyline points="16 7 22 7 22 13"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.estimatedReturns).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext green">
                    +{growthPercentage}% Growth
                  </div>
                </div>

                {/* Maturity Amount - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Maturity Amount</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <circle cx="12" cy="12" r="6"></circle>
                        <circle cx="12" cy="12" r="2"></circle>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.futureValue).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    After {tenure} years at {annualInterestRate}% interest
                  </div>
                </div>

                {/* Inflation Adjusted Value */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Inflation Adjusted Value</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.inflationAdjustedValue).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Real value in today's terms
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">RD Breakdown</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Your Deposits</span>
                      <span className="breakdown-percentage">
                        {((Number(result.totalInvestment) / Number(result.futureValue)) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${(Number(result.totalInvestment) / Number(result.futureValue)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Interest Earned</span>
                      <span className="breakdown-percentage green">
                        {((Number(result.estimatedReturns) / Number(result.futureValue)) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${(Number(result.estimatedReturns) / Number(result.futureValue)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>How Does the Recurring Deposit Calculator Work?</h3>
              <p>
                The RD calculator estimates your maturity amount by calculating compound interest on 
                your monthly deposits. Unlike FD where you invest a lump sum, RD allows you to save 
                a fixed amount every month. Interest is compounded quarterly on the accumulated 
                deposits, helping your money grow steadily over the tenure.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Formula Used:</h4>
            <div className="formula-display">
              M = P × n × (n + 1) / 2 × (r / 12 / 100)
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">M</div>
                <div className="formula-param-value">Maturity Amount</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">P</div>
                <div className="formula-param-value">Monthly Deposit</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">r</div>
                <div className="formula-param-value">Annual Interest Rate</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">n</div>
                <div className="formula-param-value">Tenure in Months</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>What is a Recurring Deposit (RD)?</h5>
              <p>
                A Recurring Deposit is a savings scheme offered by banks and post offices where you 
                deposit a fixed amount every month for a predetermined tenure. RDs offer guaranteed 
                returns with compound interest and are ideal for building disciplined savings habits. 
                They're perfect for people who want to invest small amounts regularly.
              </p>
            </div>

            <div className="faq-item">
              <h5>How is RD different from FD?</h5>
              <p>
                In FD, you invest a lump sum amount once, while in RD, you deposit a fixed amount 
                every month. FD is suitable for those with surplus funds, whereas RD helps salaried 
                individuals save regularly from their monthly income. Both offer similar interest 
                rates, but the investment pattern differs significantly.
              </p>
            </div>

            <div className="faq-item">
              <h5>What happens if I miss an RD installment?</h5>
              <p>
                Missing RD installments attracts a penalty fee, usually ₹1.50 to ₹5 per ₹100 of 
                installment per month, depending on the bank. Multiple defaults can lead to account 
                closure. Most banks allow a grace period of 1-2 months. It's important to maintain 
                sufficient balance in your linked account for auto-debit.
              </p>
            </div>

            <div className="faq-item">
              <h5>Can I withdraw my RD before maturity?</h5>
              <p>
                Yes, premature withdrawal is allowed but comes with a penalty of 1-2% on the interest 
                earned. Some banks also impose a minimum tenure before premature withdrawal is 
                permitted. The penalty varies across banks, so check your bank's specific terms. 
                Interest is calculated based on the actual period your deposits remained with the bank.
              </p>
            </div>

            <div className="faq-item">
              <h5>Are RD returns taxable?</h5>
              <p>
                Yes, RD interest is fully taxable according to your income tax slab. Banks deduct TDS 
                at 10% if annual interest exceeds ₹40,000 (₹50,000 for senior citizens). You can 
                submit Form 15G/15H to avoid TDS if your total income is below the taxable limit. 
                Unlike NSC or PPF, RDs don't offer any tax deduction benefits.
              </p>
            </div>

            <div className="faq-item">
              <h5>What is the minimum and maximum tenure for RD?</h5>
              <p>
                Most banks offer RD with a minimum tenure of 6 months and maximum of 10 years. The 
                most popular tenures are 1, 2, 3, and 5 years. Longer tenures typically offer higher 
                interest rates. You can choose the tenure based on your financial goals and savings 
                capacity. Senior citizens often get an additional 0.5% interest rate.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecurringDepositCalculator;